import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    input,
    ViewEncapsulation,
    inject,
    output,
    ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, NG_VALIDATORS, ValidationErrors, NgModel } from '@angular/forms';
import { ControlValueAccessorValidatorBase } from '@fi-shared/control-value-accessor-validator-base';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { FiInfoIconMode } from '@fi-shared/info-icon/info-icon.component';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { isNullOrUndefined } from '../utils/is-null-or-undefined';
import { FloatLabelType, MatFormFieldAppearance } from '@angular/material/form-field';

@Component({
    selector: 'fi-name-input',
    templateUrl: './fi-name-input.component.html',
    styleUrls: ['./fi-name-input.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: FiNameInputComponent,
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: FiNameInputComponent,
            multi: true,
        },
    ],
    host: { class: 'fi-name-input', '[class.auto-width]': 'inputAutoWidthEnabled()' },
})
export class FiNameInputComponent
    extends ControlValueAccessorValidatorBase<string | IContentLanguageLabelModel[]>
    implements AfterViewInit
{
    public nameFormFieldClassName = input<string>();
    public selectedLanguageId = input<number>();
    public modelName = input<string>('Name');
    public labelTextId = input<string>();
    public isDisabled = input<boolean>(false);
    public hintTextId = input<string>();
    public requiredErrorTextId = input<string>('common_error.mandatory');
    public disabledNameTooltipText = input<string>();
    public maxlength = input<number>(200);
    public isDefaultNameShownAsPlaceholder = input<boolean>(false);
    public placeholderText = input<string>();
    public floatLabel = input<FloatLabelType>('auto');
    public isRequired = input<boolean>(true);
    public appearance = input<MatFormFieldAppearance>('outline');
    public disableMaxLength = input<boolean>(false);
    public inputAutoWidthEnabled = input<boolean>(false);
    public hideErrorMessage = input<boolean>(false);
    public isHintShown = input<boolean>(true);

    public afterViewInit = output<void>();
    public focus = output<void>();
    public blur = output<void>();

    private readonly contentLanguagesService = inject(ContentLanguagesService);
    private readonly stringNameRef = { Value: '' };

    public ngAfterViewInit(): void {
        this.afterViewInit.emit();
    }

    protected getPlaceholderText(): string {
        if (!isNullOrUndefined(this.placeholderText())) {
            return this.placeholderText();
        }
        return this.isDefaultNameShownAsPlaceholder() ? this.getDefaultNameValue() : '';
    }

    private defaultLanguageId = this.contentLanguagesService.GetDefaultLanguageId();

    protected readonly FiInfoIconMode = FiInfoIconMode;

    private getDefaultNameValue(): string {
        if (!Array.isArray(this.ngModel)) {
            return '';
        }
        return (
            this.ngModel.find((name: IContentLanguageLabelModel) => name.ContentLanguageId === this.defaultLanguageId)
                ?.Value ?? ''
        );
    }

    protected isStringNameModel(): boolean {
        return !Array.isArray(this.ngModel);
    }

    protected getLabelNames(): IContentLanguageLabelModel[] {
        return Array.isArray(this.ngModel) ? this.ngModel : [];
    }

    protected getStringNameRef(): { Value: string } {
        const value = typeof this.ngModel === 'string' ? this.ngModel : '';
        if (this.stringNameRef.Value !== value) {
            this.stringNameRef.Value = value;
        }
        return this.stringNameRef;
    }

    protected onNameChange(name: { Value: string }): void {
        if (!Array.isArray(this.ngModel)) {
            this.ngModel = name.Value;
        }
        this.doPropagateChanges();
    }

    protected removeExtraLinesOnPaste($event: ClipboardEvent, name: { Value: string }): void {
        if (this.disableMaxLength()) {
            return;
        }
        $event.preventDefault();

        const textarea = $event.target as HTMLTextAreaElement;

        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const pastedText = ($event.clipboardData?.getData('text/plain') ?? '').replace(/[\r\n]+/g, ' ');
        const selectedLength = end - start;
        const availableLength = this.maxlength() - (name.Value.length - selectedLength);
        const truncatedText = pastedText.substring(0, Math.max(0, availableLength));

        name.Value = name.Value.slice(0, start) + truncatedText + name.Value.slice(end);
        this.onNameChange(name);

        const caretPosition = start + truncatedText.length;
        requestAnimationFrame(() => {
            textarea.setSelectionRange(caretPosition, caretPosition);
        });
    }

    protected isSelectedContentLangLabelModel(labelModel: IContentLanguageLabelModel): boolean {
        return labelModel.ContentLanguageId === this.selectedLanguageId();
    }

    protected isNameRequired(): boolean {
        return (
            this.isRequired() &&
            (!Array.isArray(this.ngModel)
                ? !this.ngModel?.length
                : !this.ngModel.find((name: IContentLanguageLabelModel) => name.Value))
        );
    }

    protected isMaxLengthExceeded(): boolean {
        if (this.disableMaxLength()) {
            return false;
        }
        if (!Array.isArray(this.ngModel)) {
            return this.ngModel?.length > this.maxlength();
        }
        return this.ngModel.some((name: IContentLanguageLabelModel) => name.Value?.length > this.maxlength());
    }

    public doValidate(): ValidationErrors {
        return {
            required: this.isNameRequired(),
            maxlength: this.isMaxLengthExceeded(),
        };
    }
}
